import React, {useEffect, useState} from "react";
import {ChangeOdometerModalProps} from "@/components/ChangeOdometerModal/ChangeOdometerModal.types";
import {Button, ButtonType, DateInput, Modal, NumberInput} from "@renta-apps/athenaeum-react-components";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import {DeviceCounterType} from "@/models/Enums";
import Localizer from "@/localization/Localizer";
import styles from './ChangeOdometerModal.module.scss';
import {ChangeOdometerRequest} from "@/models/server/requests/ChangeOdometerRequest";
import HttpClient from "@/common/HttpClient";
import EndpointPaths from "@/common/EndpointPaths";
import {ChangeOdometerResponse} from "@/models/server/responses/ChangeOdometerResponse";
import RentaToolsController from "@/pages/RentaToolsController";
import ToolsUtility from "@/helpers/ToolsUtility";
import moment from 'moment';
import OperatingHoursService from "@/services/OperatingHoursService";

const ChangeOdometerModal = ({modalRef, device, lastOdometerChange, refreshLastChange}: ChangeOdometerModalProps) => {
    
    const [totalOperatingHours, setTotalOperatingHours] = useState<number>(0);
    
    const [odometerChangeDate, setOdometerChangeDate] = useState<Date>(Utility.today());

    const [odometerChange, setOdometerChange] = useState<number>(0);
    
    const [responseOk, setResponseOk] = useState<boolean>(true);

    useEffect(() => {
        if(device) {
            const totalOperatingHours = device.counters?.find(p=>p.type === DeviceCounterType.TotalOperatingHours)?.value;
            
            if(totalOperatingHours) {
                setTotalOperatingHours(OperatingHoursService.getLatestValue(device));
            }
        }
    }, []);
    
    const onSave = async () => {

        if (!odometerChangeValid()) {
            return
        }

        setResponseOk(true);

        if(device) {
            const request: ChangeOdometerRequest = {
                deviceExternalId: device.externalId,
                lastOdometerRead: totalOperatingHours,
                newOdometerRead: odometerChange,
                recordedDate: odometerChangeDate
            }
            
            const response = await HttpClient.postAsync<ChangeOdometerResponse>(EndpointPaths.DevicePaths.ChangeOdometer, request);
            
            if(response.success) {
                RentaToolsController.device = response.device;
                RentaToolsController.saveContext();
                onClose();
            } else if(response.odometerChangedToday) {
                setResponseOk(false);
            }
        }
    }
    
    const onClose = async () => {
        modalRef.current?.closeAsync();
        if (refreshLastChange) {
            await refreshLastChange();
        }
    }


    const odometerChangeValid = (): boolean => {
        if(moment(lastOdometerChange?.recordDate).isSame(moment(odometerChangeDate), 'day')){
            return false;
        }
        
        if (!lastOdometerChange || !lastOdometerChange.recordDate) {
            return true;
        }
        
        return Utility.isToday(lastOdometerChange.recordDate ?? "") && !(
            totalOperatingHours == lastOdometerChange.operatingHours && odometerChange == lastOdometerChange.odometerOperatingHours // duplicating last change 
            || totalOperatingHours == lastOdometerChange.odometerOperatingHours // oph not changed since odometerchange
        );
    }

    return (
        <Modal id={"changeOdometerModal"}
                title={Localizer.odometerChangeModalTitle}
                ref={modalRef}
                closeConfirm={false}
        >
            {lastOdometerChange?.recordFound &&
                (
                    <div className={Utility.isToday(lastOdometerChange.recordDate ?? "") ? styles.lastOdometerChangeRecent : styles.lastOdometerChange}>
                        <h5>{Localizer.odometerPreviousChange}</h5>
                        <p>{Localizer.odometerOccuredOn} {ToolsUtility.toDateStringWithTime(lastOdometerChange.recordDate)}</p>
                        <p>{Localizer.dataServiceAttributesOdometerNumber}: {lastOdometerChange.odometerNumber}</p>
                        <p>{Localizer.odometerChangeModalCurrentReading}: {lastOdometerChange.odometerOperatingHours}</p>
                        <p>{Localizer.odometerChangeModalLastReading}: {lastOdometerChange.operatingHours}</p>
                    </div>
                )
            }
            
            {!responseOk &&
                <div className={styles.odometerChangeError}>
                    <span>{Localizer.odometerChangeSameDayError}</span>
                </div>
            }
            <div className={styles.inputContainer}>
                <DateInput popup
                           className={styles.date}
                           label={Localizer.odometerChangeModalDate}
                           value={odometerChangeDate}
                           onChange={async (value) => setOdometerChangeDate(value)}
                />
            </div>
            
            <div className={styles.inputContainer}>
                <NumberInput required
                             label={Localizer.odometerChangeModalLastReading}
                             format={"0.0"}
                             step={0.5}
                             min={0}
                             max={999999}
                             value={totalOperatingHours}
                             onChange={async (sender, value, userInteraction, done) => setTotalOperatingHours(value)}
                />
            </div>
            
            <div className={styles.inputContainer}>
                <NumberInput required
                             label={Localizer.odometerChangeModalCurrentReading}
                             format={"0.0"}
                             step={0.5}
                             min={0}
                             max={999999}
                             value={odometerChange}
                             onChange={async (sender, value, userInteraction, done) => setOdometerChange(value)}
                />
            </div>    

                <div className={styles.buttonContainer}>
                    <Button
                        id={"saveButton"}
                        type={ButtonType.Orange}
                        disabled={!odometerChangeValid()}
                        label={Localizer.genericSave}
                        onClick={async () => onSave()}
                    />

                    <Button
                        id={"closeButton"}
                        type={ButtonType.Blue}
                        label={Localizer.genericCancel}
                        onClick={async () => onClose()}
                    />
                </div>

        </Modal>
    )
}


export default ChangeOdometerModal;